import moment from 'moment';
import { useIntl } from 'react-intl';
import { ContentLoader } from '@rio-cloud/rio-uikit';
import type { AegVehicle } from '../../../../../models/newAEG';

type VehicleDataAsideProps = {
    vehicle: AegVehicle | null;
    isLoading: boolean;
};

type VehicleDataValue = string | boolean | undefined;

const formatDate = (date?: string, format = 'DD/MM/YYYY') => (date ? moment(date).format(format) : undefined);

const VEHICLE_DATA_FIELDS: { labelId: string; getValue: (vehicle: AegVehicle) => VehicleDataValue }[] = [
    { labelId: 'intl-msg:support.aeg.newRequest.vehicleData.owner', getValue: vehicle => vehicle.vehicleOwner },
    { labelId: 'intl-msg:support.aeg.newRequest.vehicleData.model', getValue: vehicle => vehicle.model },
    { labelId: 'intl-msg:support.aeg.newRequest.vehicleData.chassis', getValue: vehicle => vehicle.chassis },
    { labelId: 'intl-msg:support.aeg.newRequest.vehicleData.licensePlate', getValue: vehicle => vehicle.licensePlate },
    { labelId: 'intl-msg:support.aeg.newRequest.vehicleData.vehicleType', getValue: vehicle => vehicle.vehicleType },
    { labelId: 'intl-msg:support.aeg.newRequest.vehicleData.color', getValue: vehicle => vehicle.color },
    { labelId: 'intl-msg:support.aeg.newRequest.vehicleData.implement', getValue: vehicle => vehicle.implementType },
    {
        labelId: 'intl-msg:support.aeg.newRequest.vehicleData.productionDate',
        getValue: vehicle => formatDate(vehicle.productionDate),
    },
    {
        labelId: 'intl-msg:support.aeg.newRequest.vehicleData.billingDate',
        getValue: vehicle => formatDate(vehicle.billingDate),
    },
    {
        labelId: 'intl-msg:support.aeg.newRequest.vehicleData.saleDate',
        getValue: vehicle => formatDate(vehicle.saleDate),
    },
    {
        labelId: 'intl-msg:support.aeg.newRequest.vehicleData.tsDeliveryDate',
        getValue: vehicle => formatDate(vehicle.tsDeliveryDate),
    },
    {
        labelId: 'intl-msg:support.aeg.newRequest.vehicleData.responsibleDealer',
        getValue: vehicle => vehicle.responsibleDealer,
    },
    {
        labelId: 'intl-msg:support.aeg.newRequest.vehicleData.warrantyZeroEnd',
        getValue: vehicle => formatDate(vehicle.warrantyEnd),
    },
    {
        labelId: 'intl-msg:support.aeg.newRequest.vehicleData.additionalWarrantyEnd',
        getValue: vehicle => formatDate(vehicle.additionalWarrantyEnd),
    },
    {
        labelId: 'intl-msg:support.aeg.newRequest.vehicleData.maintenanceContract',
        getValue: vehicle => [vehicle.contractNumber, vehicle.contractModality].filter(Boolean).join(' - '),
    },
    {
        labelId: 'intl-msg:support.aeg.newRequest.vehicleData.specialWarranty',
        getValue: vehicle => formatDate(vehicle.specialWarrantyEnd),
    },
    { labelId: 'intl-msg:support.aeg.newRequest.vehicleData.pendingCampaigns', getValue: vehicle => vehicle.campaigns },
    {
        labelId: 'intl-msg:support.aeg.newRequest.vehicleData.checkLegal',
        getValue: vehicle => (vehicle.consultLegal === undefined ? undefined : String(vehicle.consultLegal) === 'true'),
    },
    {
        labelId: 'intl-msg:support.aeg.newRequest.vehicleData.blockedVehicle',
        getValue: vehicle => vehicle.vehicleBlocked,
    },
    { labelId: 'intl-msg:support.aeg.newRequest.vehicleData.warranty', getValue: vehicle => vehicle.hasWarranty },
    {
        labelId: 'intl-msg:support.aeg.newRequest.vehicleData.volksConfiaWarranty',
        getValue: vehicle => vehicle.hasVolksConfiaWarranty,
    },
    {
        labelId: 'intl-msg:support.aeg.newRequest.vehicleData.lastUpdate',
        getValue: vehicle => formatDate(vehicle.updatedAt, 'DD/MM/YYYY HH:mm'),
    },
];

const VehicleDataAside = ({ vehicle, isLoading }: VehicleDataAsideProps) => {
    const intl = useIntl();

    const formatValue = (value: VehicleDataValue) => {
        if (typeof value === 'boolean') {
            return intl.formatMessage({
                id: value
                    ? 'intl-msg:support.aeg.newRequest.option.yesNo.yes'
                    : 'intl-msg:support.aeg.newRequest.option.yesNo.no',
            });
        }

        return value || '-';
    };

    return (
        <section className='border rounded padding-15 lg:h-full lg:flex lg:flex-col'>
            <h3 className='margin-top-0 margin-bottom-15 text-size-16 text-bold shrink-0'>
                {intl.formatMessage({ id: 'intl-msg:support.aeg.newRequest.vehicleData.title' })}
            </h3>
            {vehicle || isLoading ? (
                <div className='display-flex flex-column lg:min-h-0 lg:overflow-y-auto lg:pr-2'>
                    {VEHICLE_DATA_FIELDS.map(({ labelId, getValue }, index) => (
                        <div
                            key={labelId}
                            className={`display-flex justify-content-between align-items-start gap-x-3 padding-y-5 text-size-14 ${
                                index < VEHICLE_DATA_FIELDS.length - 1 ? 'border border-bottom-only' : ''
                            }`}
                        >
                            <span className='text-color-dark'>{intl.formatMessage({ id: labelId })}</span>
                            {isLoading || !vehicle ? (
                                <div className='w-[80px] h-[20px] shrink-0'>
                                    <ContentLoader className='width-100pct height-100pct' />
                                </div>
                            ) : (
                                <span className='text-color-darker text-right break-all'>
                                    {formatValue(getValue(vehicle))}
                                </span>
                            )}
                        </div>
                    ))}
                </div>
            ) : (
                <div className='text-center text-color-gray text-size-14 padding-25 lg:my-auto'>
                    {intl.formatMessage({ id: 'intl-msg:support.aeg.newRequest.vehicleData.empty' })}
                </div>
            )}
        </section>
    );
};

export default VehicleDataAside;
